<template>
  <div v-if="patient.id">

    <b-card no-title no-body>
      <div class="own-header">
        <b-row>
          <b-col class="display-name" sm="12" md="6">
            <i class="fa fa-align-justify"></i>
            <strong class="header-spacing"> {{patient.title}}. {{patient.firstName}} {{patient.lastName}} </strong>
            <b-badge v-if="patient.passwordSetupPending" variant="warning" class="ml-2"
              title="The patient has not chosen a password yet">Password not set</b-badge>
          </b-col>
          <b-col sm="12" md="6">
            <b-button variant="primary" @click="showBookConsultationModal()" class="float-right">
              Book Consultation
              <b-badge variant="light" class="ml-2">NEW</b-badge>
            </b-button>
            <b-button v-if="!editable" variant="warning" @click="allowChanges()" class="float-right margin-1-percent">Update Patient</b-button>
            <b-button v-if="!editable && patient.email" variant="outline-primary" :disabled="isSendingPasswordLink"
              @click="sendPasswordLink()" class="float-right margin-1-percent">
              <span v-if="isSendingPasswordLink">Sending...</span>
              <span v-else>{{ patient.passwordSetupPending ? 'Send set-password link' : 'Send password reset link' }}</span>
            </b-button>
            <b-button v-if="editable" variant="success" @click="updatePatient()" class="float-right margin-1-percent">Confirm</b-button>
            <b-button v-if="editable" variant="outline-danger" @click="reset()" class="float-right margin-1-percent">Reset</b-button>
          </b-col>
        </b-row>
      </div>
      <b-card-body>
        <b-row>
          <b-col md="12" lg="6">
            <b-row>
              <b-col sm="12" md="4" lg="12">
                <b-form-group id="id" label="Patient ID" label-for="id">
                  <b-form-input name="id" type="text" v-model="patient.id" disabled />
                </b-form-group>
              </b-col>

            </b-row>
            <b-row>
              <b-col sm="6" md="4" lg="3">
                <b-form-group id="active" label="Active" label-for="active">
                  <b-form-radio-group v-if="editable" :block="true" button-variant="outline-primary" buttons v-model="patient.active" name="active" :options="truthyFalsy"/>
                  <b-button-toolbar v-if="!editable">
                    <b-button v-bind:variant="patient.active ? 'primary' : 'outline-primary'" name="active" type="text" disabled> Yes </b-button>
                    <b-button v-bind:variant="!patient.active ? 'primary' : 'outline-primary'" name="active" type="text" disabled> No </b-button>
                  </b-button-toolbar>
                </b-form-group>
              </b-col>

              <b-col sm="6" md="4" lg="3">
                <b-form-group id="marketingConsent" label="Marketing" label-for="marketingConsent">
                  <b-form-radio-group v-if="editable  && !patientManaged" :block="true" button-variant="outline-primary" buttons v-model="patient.marketingConsent" name="marketing" :options="truthyFalsy"/>
                  <b-button-toolbar v-if="!editable || patientManaged">
                    <b-button v-bind:variant="patient.marketingConsent ? 'primary' : 'outline-primary'" name="active" type="text" disabled> Yes </b-button>
                    <b-button v-bind:variant="!patient.marketingConsent ? 'primary' : 'outline-primary'" name="active" type="text" disabled> No </b-button>
                  </b-button-toolbar>
                </b-form-group>
              </b-col>

              <b-col sm="6" md="4" lg="4">
                <b-form-group id="gender" label="Gender" label-for="gender">
                  <b-form-radio-group v-if="editable" :block="true" button-variant="outline-primary" buttons v-model="patient.gender" name="gender" :options="genderOptions"/>
                  <b-button-toolbar v-if="!editable">
                    <b-button v-bind:variant="patient.gender === 'MALE' ? 'primary' : 'outline-primary'" name="active" type="text" disabled> Male </b-button>
                    <b-button v-bind:variant="patient.gender === 'FEMALE' ? 'primary' : 'outline-primary'" name="active" type="text" disabled> Female </b-button>
                  </b-button-toolbar>
                </b-form-group>
              </b-col>

              <b-col sm="6" md="4" lg="3">
                <b-form-group id="shareNotes" label="Share notes" label-for="shareNotes">
                  <b-form-radio-group v-if="editable && !patientManaged" :block="true" button-variant="outline-primary" buttons v-model="patient.preferences.shareDetailsWithGp" name="shareNotes" :options="truthyFalsy"/>
                  <b-button-toolbar v-if="!editable || patientManaged">
                    <b-button v-bind:variant="patient.preferences.shareDetailsWithGp == 'true' ? 'primary' : 'outline-primary'" name="shareNotes" type="text" disabled> Yes </b-button>
                    <b-button v-bind:variant="patient.preferences.shareDetailsWithGp == 'false' ? 'primary' : 'outline-primary'" name="shareNotes" type="text" disabled> No </b-button>
                  </b-button-toolbar>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col v-if="!isActivePatient && patient.active">
                <b-form-group id="PATIENT_ACCOUNT_ENABLED_REASON" label="Patient Activation Reason" label-for="PATIENT_ACCOUNT_ENABLED_REASON" label-cols="5">
                  <b-form-input name="PATIENT_ACCOUNT_ENABLED_REASON" type="text" v-model="patient.settings.PATIENT_ACCOUNT_ENABLED_REASON" v-validate="'required'" :disabled="!editable"/>
                  <span v-show="errors.has('PATIENT_ACCOUNT_ENABLED_REASON')" class="text-danger">{{ errors.first('PATIENT_ACCOUNT_ENABLED_REASON') }}</span>
                </b-form-group>
              </b-col>
            </b-row>

            <b-form-select v-model="patient.title" :options="titleOptions" class="mb-3" :disabled="!editable"/>

            <b-row>
              <b-col md="12" lg="6">
                <b-form-group id="firstName" label="First Name" label-for="firstName">
                  <b-form-input name="firstName" type="text" v-model="patient.firstName" v-validate="'required'" :disabled="!editable"/>
                  <span v-show="errors.has('firstName')" class="text-danger">{{ errors.first('firstName') }}</span>
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group id="lastName" label="Last Name" label-for="lastName">
                  <b-form-input name="lastName" type="text" v-model="patient.lastName" v-validate="'required'" :disabled="!editable"/>
                  <span v-show="errors.has('lastName')" class="text-danger">{{ errors.first('lastName') }}</span>
                </b-form-group>
              </b-col>
            </b-row>
            <b-form-group id="email" label="Email" label-for="email" label-cols="4">
              <b-form-input name="email" type="text" v-model="patient.email" v-validate="'required|email'" :disabled="!editable"/>
              <span v-show="errors.has('email')" class="text-danger">{{ errors.first('email') }}</span>
            </b-form-group>
            <b-form-group id="phone" label="Phone" label-for="phone" label-cols="4">
              <b-form-input name="phone" type="text" v-model="patient.phone" v-validate="'required'" :disabled="!editable"/>
              <span v-show="errors.has('phone')" class="text-danger">{{ errors.first('phone') }}</span>
            </b-form-group>
            <b-form-group id="dob" label="Date of Birth" label-for="dob" label-cols="4">
              <b-form-input name="dob" type="text" v-model="patient.dateOfBirth" v-validate="'required'" :disabled="!editable"/>
              <span v-show="errors.has('dob')" class="text-danger">{{ errors.first('dob') }}</span>
            </b-form-group>
            <b-form-group id="platform" label="Platform" label-for="platform" label-cols="4">
              <b-form-input name="platform" type="text" v-model="patient.platform" v-validate="'required'" :disabled="true"/>
              <span v-show="errors.has('platform')" class="text-danger">{{ errors.first('platform') }}</span>
            </b-form-group>
          </b-col>

          <b-col>
            <b-form-group id="line1" label="Line 1" label-for="line1">
              <b-form-input name="line1" type="text" v-model="patient.address.line1" :disabled="!editable"/>
              <span v-show="errors.has('line1')" class="text-danger">{{ errors.first('line1') }}</span>
            </b-form-group>
            <b-form-group id="line2" label="Line 2" label-for="line2">
              <b-form-input name="line2" type="text" v-model="patient.address.line2" :disabled="!editable"/>
            </b-form-group>
            <b-form-group id="county" label="County" label-for="county">
              <b-form-input name="county" type="text" v-model="patient.address.county" :disabled="!editable"/>
            </b-form-group>
            <b-form-group id="city" label="City" label-for="city">
              <b-form-input name="city" type="text" v-model="patient.address.city" :disabled="!editable"/>
              <span v-show="errors.has('city')" class="text-danger">{{ errors.first('city') }}</span>
            </b-form-group>
            <b-form-group id="postcode" label="Postcode" label-for="postcode">
              <b-form-input name="postcode" type="text" v-model="patient.address.postcode" v-validate="'required'" :disabled="!editable"/>
              <span v-show="errors.has('postcode')" class="text-danger">{{ errors.first('postcode') }}</span>
            </b-form-group>
            </b-col>
          </b-row>

          <br/>
          <b-row>
            <b-col>
            <div v-if="patient.settings">
              <b-row>
                <b-col md="4" class="mb-1">
                  <b-form-group id="EXTERNALLY_IDENTIFIED" label="Externally Identified" label-for="EXTERNALLY_IDENTIFIED_BY" label-cols="6" >
                    <b-form-radio-group v-if="editable" button-variant="outline-primary" buttons v-model="patient.settings.EXTERNALLY_IDENTIFIED" name="EXTERNALLY_IDENTIFIED" :options="truthyFalsy"/>
                    <b-button-toolbar>
                      <b-button v-if="!editable" v-bind:variant="patient.settings.EXTERNALLY_IDENTIFIED ? 'primary' : 'outline-primary'" name="active" type="text" disabled> Yes </b-button>
                      <b-button v-if="!editable" v-bind:variant="!patient.settings.EXTERNALLY_IDENTIFIED ? 'primary' : 'outline-primary'" name="active" type="text" disabled> No </b-button>
                    </b-button-toolbar>
                  </b-form-group>
                </b-col>
                <b-col v-if="patient.settings.EXTERNALLY_IDENTIFIED">
                  <b-form-group id="EXTERNALLY_IDENTIFIED_BY" label="Externally Identified By" label-for="EXTERNALLY_IDENTIFIED_BY" label-cols="5">
                    <b-form-input name="EXTERNALLY_IDENTIFIED_BY" type="text" v-model="patient.settings.EXTERNALLY_IDENTIFIED_BY" v-validate="externalIdentificationRule" :disabled="!editable"/>
                    <span v-show="errors.has('EXTERNALLY_IDENTIFIED_BY')" class="text-danger">{{ errors.first('EXTERNALLY_IDENTIFIED_BY') }}</span>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-form-group v-if="patient.settings.LOGIN_ATTEMPTS" id="LOGIN_ATTEMPTS" label="Login attempts" label-for="LOGIN_ATTEMPTS" label-cols="5">
                <b-row>
                  <b-col md="4" class="mb-1">
                    <b-form-input name="LOGIN_ATTEMPTS" type="text" v-model="patient.settings.LOGIN_ATTEMPTS" disabled/>
                  </b-col>
                  <b-col>
                    <b-button block type="button" variant="outline-danger" @click="patient.settings.LOGIN_ATTEMPTS = ''" :disabled="!editable">Clear login attempts</b-button>
                  </b-col>
                </b-row>
              </b-form-group>
              <b-form-group v-if="patient.settings.COUPON_ATTEMPTS" id="COUPON_ATTEMPTS" label="Coupon attempts" label-for="COUPON_ATTEMPTS" label-cols="5">
                <b-row>
                  <b-col md="4" class="mb-1">
                    <b-form-input name="COUPON_ATTEMPTS" type="text" v-model="patient.settings.COUPON_ATTEMPTS" disabled/>
                  </b-col>
                  <b-col>
                    <b-button block type="button" variant="outline-danger" @click="patient.settings.COUPON_ATTEMPTS=''" :disabled="!editable">Clear coupon attempts</b-button>
                  </b-col>
                </b-row>
              </b-form-group>
              <b-form-group id="associateWithAnEnterprise" label="Enterprise" label-for="associateWithAnEnterprise" label-cols="5">
                <b-button v-if="!associateWithAnEnterprise" type="button" block variant="outline-primary" @click="associateWithAnEnterprise = true" :disabled="!editable">Add enterprise</b-button>
                <b-row v-if="associateWithAnEnterprise">
                  <b-col md="4" class="mb-1">
                    <b-form-input name="ENTERPRISE" type="text" v-model="patient.settings.ENTERPRISE" v-validate="enterpriseRule" :disabled="!editable"/>
                    <span v-show="errors.has('ENTERPRISE')" class="text-danger">{{ errors.first('ENTERPRISE') }}</span>
                  </b-col>
                  <b-col>
                    <b-button block type="button" variant="outline-danger" @click="removeEnterprise()" :disabled="!editable">
                      Remove enterprise
                    </b-button>
                  </b-col>
                </b-row>
              </b-form-group>
            </div>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

     <b-card no-title no-body>
      <div class="own-header">
        <b-row>
          <b-col class="display-name" sm="12" md="6">
            <i class="fa fa-align-justify"></i>
            <strong class="header-spacing">Linked Patients</strong>
          </b-col>
        </b-row>
      </div>
      <b-card-body>
        <b-row class="mt-2 mrgx-6">
          <b-col 
              v-for="linkedPatient in linkedPatients" 
              :key="linkedPatient.orderItemId" 
              sm="12" 
              md="6" 
              lg="4" 
              xl="3"
              class="mb-3 px-2"
            >
              <div class="linked-patient-card">
                <div class="patient-details-wrapper">
                  <b-row class="align-items-center title-wrapper">
                    <label class="patient-card-heading">{{ linkedPatient.patientName }}</label>
                  </b-row>
                  <b-row class="align-items-center">
                    <label class="freeSub link-patient-text">{{ linkedPatient.formattedDob }} | Age: {{ linkedPatient.age }}</label>
                  </b-row>
                  <b-row class="align-items-center">
                    <label class="freeSub link-patient-text font-italic">{{ linkedPatient.relationship }}</label>
                  </b-row>
                  <b-row class="align-items-center">
                    <router-link :to="{path: '/order/' + linkedPatient.orderItemId }">
                      <b-button variant="warning view-btn">View Order</b-button>
                    </router-link>
                  </b-row>
                </div>
            </div>
          </b-col>
          
          <b-col v-if="linkedPatients.length === 0" class="text-center py-3">
            <label class="freeSub link-patient-text">No linked patients found</label>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Admin Key Alert -->
    <b-card no-title no-body>
      <div class="own-header">
        <b-row>
          <b-col class="display-name" sm="12" md="6">
            <i class="fa fa-align-justify"></i>
            <strong class="header-spacing">Admin Key Alert</strong>
          </b-col>
          <b-col sm="12" md="6">
            <b-button v-if="!adminAlertEditable" variant="warning" @click="toggleAdminAlertEditable()" class="float-right">Update Alert</b-button>
            <b-button v-if="adminAlertEditable" variant="success" @click="updateAdminAlert()" class="float-right">Confirm</b-button>
            <b-button v-if="adminAlertEditable" variant="outline-danger" @click="toggleAdminAlertEditable()" class="float-right margin-1-percent">Cancel</b-button>
          </b-col>
        </b-row>
      </div>
      <b-card-body>
        <b-row>
          <b-col sm="12">
            <b-form-group id="adminKeyAlert" label="Information about the patient that all clinicians should be aware of:" label-for="adminKeyAlert">
              <b-form-textarea name="adminKeyAlert" type="text" class="text-left"
                               :maxlength="250"
                               v-model="tempAdminAlert" :disabled="!adminAlertEditable"/>
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Orders of this patient-->
    <orders-table title="Orders" :orders="orders" :page="currentPage" :action="action" :click-event="clickEvent" :column-names="columnNames"></orders-table>

    <b-modal ref="book-consultation-modal" hide-footer size="lg"
      @hide="resetBookConsultationData">
      <template slot="modal-title">
        Book Consultation
        <b-badge variant="success" class="ml-2">NEW</b-badge>
      </template>
      <b-container fluid class="book-consultation-modal">
        <p class="book-help-text">
          Select a slot and send a payment link to the patient, the same way additional payment links are sent.
          They complete payment on the patient app. A 100% coupon still books the slot immediately.
        </p>
        <div class="input-container">
          <div class="input-section">
            <p class="model-text">Service</p>
            <b-form-radio-group v-model="bookingTenantId" :options="tenantOptions" buttons button-variant="outline-primary" @change="onBookingTenantChange" />
          </div>
          <div class="input-section">
            <p class="model-text">Consultation type</p>
            <b-form-radio-group v-model="bookingMediaType" :options="mediaTypeOptions" buttons button-variant="outline-primary" />
          </div>
          <div class="input-section">
            <p class="model-text">Please select an appointment date:</p>
            <b-form-datepicker v-model="bookingSelectedDate" dropdown
              :min="bookingMinDate"
              :date-format-options="{ year: 'numeric', month: 'short', day: 'numeric' }" locale="en-GB"
              class="mb-2"></b-form-datepicker>
          </div>
          <div class="input-section">
            <p class="model-text">Selected clinician</p>
            <div class="custom-select-preview">
              <p v-if="isLoadingBookingDoctors" class="clinician-loading">
                <i class="fa fa-spinner fa-spin"></i> Loading clinicians…
              </p>
              <multiselect
                v-else
                v-model="bookingSelectedDoctorOption"
                :options="bookingDoctorMultiselectOptions"
                :searchable="true"
                :internal-search="true"
                :allow-empty="true"
                :show-labels="false"
                :loading="isLoadingBookingDoctors"
                placeholder="Search or select a clinician"
                label="text"
                track-by="value"
                class="clinician-multiselect"
                @input="onBookingDoctorMultiselect"
              >
                <template slot="noOptions">No clinicians found</template>
                <template slot="noResult">No clinicians match your search</template>
              </multiselect>
              <b-form-checkbox v-model="bookingShowAllClinicians" class="mt-1 small">
                Show inactive clinicians too
              </b-form-checkbox>
              <div class="time-slots-container" v-if="bookingSelectedDoctor">
                <div class="note-tag" :class="{ 'note-tag-success': bookingSelectedDoctorDoesPrescription }">
                  Prescription
                </div>
                <div class="note-tag" :class="{ 'note-tag-success': bookingSelectedDoctorDoesFitNote }">
                  Fit Note
                </div>
                <div class="note-tag" :class="{ 'note-tag-success': bookingSelectedDoctorDoesReferralLetter }">
                  Referral Letter
                </div>
              </div>
            </div>
          </div>
          <div class="input-section">
            <p class="model-text">Please select an appointment time:</p>
            <p v-if="!bookingSelectedDoctor" class="no-slots">Select a clinician to see available times</p>
            <p v-else-if="isLoadingBookingSlots" class="no-slots">
              <i class="fa fa-spinner fa-spin"></i> Loading slots…
            </p>
            <div class="time-slots-container" v-else-if="bookingTimeSlots.length > 0">
              <p v-for="appointment in bookingTimeSlots" :key="appointment.id" class="time-slot"
                :class="{ 'selected-slot': isBookingSlotSelected(appointment) }"
                @click="selectBookingTimeSlot(appointment)">
                {{ formatBookingTime(appointment.datetime) }}
              </p>
            </div>
            <p v-else class="no-slots">No available time slots found</p>
          </div>
          <div class="input-section">
            <p class="model-text">Coupon code (optional)</p>
            <b-input-group>
              <b-form-input v-model="bookingCouponId" placeholder="Enter a coupon code" @keydown.enter.prevent="applyBookingCoupon" />
              <b-input-group-append>
                <b-button variant="outline-primary" :disabled="isApplyingCoupon" @click="applyBookingCoupon">
                  {{ isApplyingCoupon ? 'Checking...' : 'Apply' }}
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <p v-if="couponCoversFullFee" class="coupon-success">100% coupon applied. The slot will be booked without a payment link.</p>
            <p v-else-if="appliedCouponPercent > 0" class="coupon-success">{{ Math.round(appliedCouponPercent * 100) }}% coupon applied. It will be included on the payment link.</p>
          </div>
          <div class="input-section" v-if="!couponCoversFullFee">
            <p class="model-text">Payment</p>
            <p class="saved-card-text">
              A payment link will be emailed to {{ patient.email || 'the patient' }}.
              The slot is held for <strong>10 minutes</strong> — they must pay within that time, or the link expires and the slot goes back on sale.
            </p>
          </div>
          <div class="input-section">
            <p class="model-text">Notes for clinicians (optional)</p>
            <b-form-textarea v-model="bookingNotes" rows="3" maxlength="500"
              placeholder="Reason for booking or anything the clinician should know" />
          </div>
          <b-alert :show="!!bookingErrorMessage" variant="danger">{{ bookingErrorMessage }}</b-alert>
          <b-button class="update-button" :disabled="isBookingConsultation" @click="submitBookConsultation">
            {{ bookingSubmitLabel }}
          </b-button>
        </div>
      </b-container>
    </b-modal>
    <payment-link-modal ref="paymentLinkModal" />
  </div>
</template>

<script>

import moment from 'moment'
import OrdersTable from '../../components/OrdersTable'
import PaymentLinkModal from '../../components/PaymentLinkModal'
import OrderEventBus from '../../EventBus.js'
import publicService from '../../services/PublicService'

export default {
  name: 'Orders',
  components: {
    OrdersTable,
    PaymentLinkModal
  },
  data: function () {
    return {
      patient: {},
      linkedPatients: [],
      editable: false,
      patientManaged: false,
      isActivePatient: false,
      adminAlertEditable: false,
      tempAdminAlert: '',
      postcodeLocation: {},
      showMap: false,
      truthyFalsy: [
        { text: 'Yes', value: true },
        { text: 'No', value: false }
      ],
      genderOptions: [
        { text: 'Male', value: 'MALE' },
        { text: 'Female', value: 'FEMALE' }
      ],
      titleOptions: [
        { text: 'Mr', value: 'Mr' },
        { text: 'Mrs', value: 'Mrs'},
        { text: 'Miss', value: 'Miss'},
        { text: 'Dr', value: 'Dr'},
        { text: 'Prof', value: 'Prof'},
        { text: 'Ms', value: 'Ms'}
      ],
      associateWithAnEnterprise: false,
      orders: {},
      currentPage: 1,
      action: "<button class='btn btn-sm btn-outline-primary'>Open Order</button>",
      columnNames: ['id', 'creationDate', 'status', 'name', 'clinician', 'pharmacy', 'actions'],
      clickEvent: 'open-order-click',
      newOrderSound: new Audio('/static/message.mp3'),
      timer: '',
      bookingTenantId: 'GPS',
      bookingMediaType: 'VIDEO',
      bookingSelectedDate: moment().format('YYYY-MM-DD'),
      bookingMinDate: moment().format('YYYY-MM-DD'),
      bookingSelectedDoctor: null,
      bookingSelectedDoctorOption: null,
      bookingDoctors: [],
      bookingShowAllClinicians: false,
      bookingTimeSlots: [],
      bookingSelectedAppointment: null,
      bookingCouponId: '',
      bookingNotes: '',
      bookingErrorMessage: '',
      isLoadingBookingDoctors: false,
      isLoadingBookingSlots: false,
      isBookingConsultation: false,
      isSendingPasswordLink: false,
      isApplyingCoupon: false,
      couponCoversFullFee: false,
      appliedCouponPercent: 0,
      bookingSelectedDoctorDoesFitNote: false,
      bookingSelectedDoctorDoesPrescription: false,
      bookingSelectedDoctorDoesReferralLetter: false,
      tenantOptions: [
        { text: 'GP Service', value: 'GPS' },
        { text: 'Goodbody', value: 'GB' }
      ],
      mediaTypeOptions: [
        { text: 'Video', value: 'VIDEO' },
        { text: 'Audio', value: 'AUDIO' }
      ]
    }
  },
  watch: {
    currentPage: function () {
      this.getPatientOrders();
    },
    bookingSelectedDate: function () {
      this.fetchBookingSlots();
    },
    bookingSelectedDoctor: function (doctorId) {
      this.onBookingDoctorSelected(doctorId);
      this.fetchBookingSlots();
    },
    bookingShowAllClinicians: function () {
      if (this.bookingSelectedDoctor
          && !this.bookingDoctorMultiselectOptions.some(option => option.value === this.bookingSelectedDoctor)) {
        this.bookingSelectedDoctor = null;
        this.bookingSelectedDoctorOption = null;
      }
    },
    bookingCouponId: function (value) {
      if (!(value || '').trim()) {
        this.couponCoversFullFee = false;
        this.appliedCouponPercent = 0;
      }
    }
  },
  computed: {
    externalIdentificationRule () {
      return this.patient.settings.EXTERNALLY_IDENTIFIED ? 'required' : '';
    },
    enterpriseRule () {
      return this.associateWithAnEnterprise ? 'required' : '';
    },
    bookingDoctorOptions () {
      return this.bookingDoctors
        .filter(availableDoctor => availableDoctor && availableDoctor.doctor)
        .filter(availableDoctor => this.bookingShowAllClinicians
          || availableDoctor.doctor.activeClinician !== false
          || availableDoctor.doctor.id === this.bookingSelectedDoctor)
        .map(availableDoctor => ({
          value: availableDoctor.doctor.id,
          text: availableDoctor.doctor.displayName
        }));
    },
    bookingDoctorMultiselectOptions () {
      return this.bookingDoctorOptions.filter(option => option && option.value != null);
    },
    bookingSubmitLabel () {
      if (this.isBookingConsultation) {
        return this.couponCoversFullFee ? 'Booking...' : 'Sending payment link...';
      }
      return this.couponCoversFullFee ? 'Book Consultation' : 'Send payment link';
    }
  },
  mounted: function () {
    this.getPatient();
     this.getSecondaryOrders(); 
    OrderEventBus.$on('currentPage', (currentPage) => {
      this.currentPage = currentPage;
    })
    OrderEventBus.$on(this.clickEvent, (orderId) => {
      this.$router.push('/order/' + orderId)
    })
  },
  methods: {
    getPatient () {
      this.$patientService.getPatient(this.$route.params.patientId).then(patient => {
        this.patient = patient;

        if (this.patient.active) {
          this.isActivePatient = true;
        }

        // DoB conversion
        if(this.patient.dateOfBirth){
          this.patient.dateOfBirth = moment(this.patient.dateOfBirth).format('DD MMM YYYY')
        }

        this.convertSettings();
        this.getPatientOrders();

        this.patientManaged = this.patient.preferences.disabledByPatient === 'true';

        this.tempAdminAlert =  this.patient?.settings?.ADMIN_ALERTS || this.patient?.allAlerts?.admin || this.patient?.adminAlerts || '';

        if (this.$route.query.book === '1') {
          this.$nextTick(() => {
            this.showBookConsultationModal();
            const query = Object.assign({}, this.$route.query);
            delete query.book;
            this.$router.replace({ path: this.$route.path, query });
          });
        }
      })
    },
    getSecondaryOrders() {
      this.$orderService.getSecondaryOrders(this.$route.params.patientId)
        .then(response => {
          if (response.status === 'OK' && response.data) {
            const patientPromises = response.data.map(item => 
              this.$orderService.getOrder(item.orderItemId)
                .then(orderResponse => {
                  if (orderResponse && orderResponse.patient) {
                    const patient = orderResponse.patient;
                    
                    let formattedDob = 'Unknown';
                    if (patient.dateOfBirth) {
                      formattedDob = moment(patient.dateOfBirth).format('DD-MMM-YYYY');
                    }
                    
                    let formattedOrderDate = moment(item.date).format('DD-MMM-YYYY');
                    
                    return {
                      orderItemId: item.orderItemId,
                      date: formattedOrderDate,
                      patientId: patient.id,
                      patientName: patient.displayName,
                      patientDob: patient.dateOfBirth,
                      formattedDob: formattedDob, 
                      age: patient.dateOfBirth ? 
                          moment().diff(moment(patient.dateOfBirth), 'years') : 
                          'Unknown',
                      patientEmail: patient.email,
                      patientPhone: patient.phone,
                      relationship: orderResponse.bookerPatientRelation
                    };
                  }
                  return null;
                })
                .catch(error => {
                  console.error(`Error fetching order ${item.orderItemId}:`, error);
                  return null;
                })
            );

            return Promise.all(patientPromises);
          }
          return [];
        })
        .then(linkedPatientsWithDetails => {
          this.linkedPatients = linkedPatientsWithDetails.filter(patient => patient !== null);
        })
        .catch(error => {
          console.error('Error fetching secondary orders:', error);
          this.$awn.alert('Error loading linked patient orders');
        });
    },
    convertSettings () {
      if (this.patient.settings.EXTERNALLY_IDENTIFIED === "true") {
        this.$set(this.patient.settings, 'EXTERNALLY_IDENTIFIED', true);
      } else {
        this.$set(this.patient.settings, 'EXTERNALLY_IDENTIFIED', false);
      }
      this.associateWithAnEnterprise = this.patient.settings.ENTERPRISE ? true : false;
    },
    allowChanges() {
      this.editable = true;
      this.truthyFalsy.forEach(option => {
        this.$delete(option, 'disabled');
      })
    },
    reset() {
      this.truthyFalsy.forEach(option => {
        this.$set(option, 'disabled');
      })
      this.editable = false;
      this.getPatient();
    },
    updatePatient () {
      this.prepareToUpdate()
      this.$validator.validateAll().then((success) => {
        if (success) {
          // fix dob for backend
          if(this.patient.dateOfBirth){
            this.patient.dateOfBirth = moment(this.patient.dateOfBirth).format('YYYY-MM-DD')
          }

          this.$patientService.updatePatient(this.patient).then(() => {
            this.$router.push('/patients');
            this.$awn.success('Patient updated successfully');
          }).catch((error) => {
            console.log(error.response)
            if (error.response && error.response.data) {
              this.$awn.alert(this.concatErrorMessage(error.response.data.message));
            } else {
              this.$awn.alert('There was a network error');
            }
          });
        } else {
          this.$awn.alert('Please check the form above for any missing fields or errors.');
        }
      })
    },
    prepareToUpdate () {
      this.patient.username = this.patient.email;
      this.patient.password = "qwerty";
      delete this.patient.displayName;
      if (this.patient.settings.EXTERNALLY_IDENTIFIED) {
        this.patient.settings.EXTERNALLY_IDENTIFIED = "true";
      } else {
        this.patient.settings.EXTERNALLY_IDENTIFIED = "false";
        this.patient.settings.EXTERNALLY_IDENTIFIED_BY = "";
      }
      if (this.patient.active && this.patientManaged) {
        this.$set(this.patient.settings, 'PATIENT_ACCOUNT_ENABLED_REASON', this.patient.settings.PATIENT_ACCOUNT_ENABLED_REASON);
      }
    },
    removeEnterprise () {
      this.associateWithAnEnterprise = false;
      this.patient.settings.ENTERPRISE = '';
    },
    concatErrorMessage (message) {
      let errorMessage = message + ". ";
      return errorMessage;
    },
    getPatientOrders () {
      this.$orderService.getPatientOrders(this.patient.id, this.currentPage - 1, 10).then(orders => {
        if (this.orders.totalElements < orders.totalElements) {
          this.newOrderSound.play();
        }
        this.orders = orders;
        this.timer = setTimeout(this.getPatientOrders, 60000);
      }).catch(error => {
        console.log(error);
        this.$awn.alert('There was an error getting orders');
      });
    },
    toggleAdminAlertEditable() {
      if (!this.adminAlertEditable) {
        this.adminAlertEditable = true;
      } else {
        this.tempAdminAlert =  this.patient?.settings?.ADMIN_ALERTS || this.patient?.allAlerts?.admin || this.patient?.adminAlerts || '';
        this.adminAlertEditable = false;
      }
    },
    showBookConsultationModal () {
      this.resetBookConsultationData();
      this.bookingSelectedDate = moment().format('YYYY-MM-DD');
      this.setBookingTenantHeader();
      this.fetchBookingDoctors();
      this.$refs['book-consultation-modal'].show();
    },
    resetBookConsultationData () {
      this.bookingErrorMessage = '';
      this.bookingSelectedAppointment = null;
      this.bookingSelectedDoctor = null;
      this.bookingSelectedDoctorOption = null;
      this.bookingTimeSlots = [];
      this.bookingCouponId = '';
      this.bookingNotes = '';
      this.isBookingConsultation = false;
      this.isLoadingBookingSlots = false;
      this.isApplyingCoupon = false;
      this.couponCoversFullFee = false;
      this.appliedCouponPercent = 0;
      sessionStorage.removeItem('tenantId');
    },
    applyBookingCoupon () {
      const couponId = (this.bookingCouponId || '').trim();
      this.couponCoversFullFee = false;
      this.appliedCouponPercent = 0;
      if (!couponId) {
        this.bookingErrorMessage = 'Enter a coupon code first.';
        return;
      }
      this.isApplyingCoupon = true;
      publicService.getCoupon(couponId)
        .then(result => {
          if (!result.success || !result.data) {
            this.bookingErrorMessage = result.message || 'Invalid coupon';
            return;
          }
          const coupon = result.data.data || result.data;
          const percentage = Number(coupon.percentage);
          this.appliedCouponPercent = isNaN(percentage) ? 0 : percentage;
          this.couponCoversFullFee = this.appliedCouponPercent >= 1;
          this.bookingErrorMessage = '';
        })
        .finally(() => {
          this.isApplyingCoupon = false;
        });
    },
    setBookingTenantHeader () {
      sessionStorage.setItem('tenantId', this.bookingTenantId);
    },
    onBookingTenantChange () {
      this.bookingSelectedAppointment = null;
      this.bookingTimeSlots = [];
      this.setBookingTenantHeader();
      this.fetchBookingDoctors();
      this.fetchBookingSlots();
    },
    fetchBookingDoctors () {
      const fromDate = moment().format('YYYY-MM-DD');
      const toDate = moment().add(21, 'days').format('YYYY-MM-DD');
      this.isLoadingBookingDoctors = true;
      this.$doctorService.getAvailabilities(fromDate, toDate)
        .then(data => {
          this.bookingDoctors = Array.isArray(data) ? data : [];
          if (this.bookingSelectedDoctor) {
            this.onBookingDoctorSelected(this.bookingSelectedDoctor);
            this.bookingSelectedDoctorOption = this.bookingDoctorMultiselectOptions
              .find(option => option.value === this.bookingSelectedDoctor) || null;
          } else {
            this.bookingSelectedDoctorOption = null;
          }
        })
        .catch(error => {
          console.error('Error fetching clinicians:', error);
          this.bookingDoctors = [];
          this.bookingSelectedDoctorOption = null;
          this.bookingErrorMessage = 'Could not load clinicians.';
        })
        .finally(() => {
          this.isLoadingBookingDoctors = false;
        });
    },
    onBookingDoctorMultiselect (option) {
      this.bookingSelectedDoctor = option && option.value != null ? option.value : null;
    },
    onBookingDoctorSelected (selectedDoctorId) {
      const selectedDoctorData = this.bookingDoctors.find(d => d.doctor && d.doctor.id === selectedDoctorId);
      if (selectedDoctorData && selectedDoctorData.doctor) {
        this.bookingSelectedDoctorDoesFitNote = !!selectedDoctorData.doctor.doesFitNote;
        this.bookingSelectedDoctorDoesPrescription = !!selectedDoctorData.doctor.doesPrescription;
        this.bookingSelectedDoctorDoesReferralLetter = !!selectedDoctorData.doctor.doesReferralLetter;
      } else {
        this.bookingSelectedDoctorDoesFitNote = false;
        this.bookingSelectedDoctorDoesPrescription = false;
        this.bookingSelectedDoctorDoesReferralLetter = false;
      }
    },
    fetchBookingSlots () {
      if (!this.bookingSelectedDoctor || !this.bookingSelectedDate) {
        this.bookingTimeSlots = [];
        return;
      }
      this.setBookingTenantHeader();
      this.isLoadingBookingSlots = true;
      this.bookingSelectedAppointment = null;
      this.$doctorService.getDoctorAppointments(this.bookingSelectedDoctor)
        .then(data => {
          const slots = data && data.data ? data.data : [];
          const selectedDate = moment(this.bookingSelectedDate).format('YYYY-MM-DD');
          const from = moment(selectedDate + ' 00:00:00').toDate();
          const to = moment(selectedDate + ' 23:59:59').toDate();
          this.bookingTimeSlots = slots.filter(item => {
            const itemDate = new Date(item.datetime);
            return itemDate >= from && itemDate <= to && itemDate > new Date();
          });
        })
        .catch(error => {
          console.error('Error fetching appointment slots:', error);
          this.bookingTimeSlots = [];
          this.bookingErrorMessage = 'Could not load appointment slots.';
        })
        .finally(() => {
          this.isLoadingBookingSlots = false;
        });
    },
    selectBookingTimeSlot (appointment) {
      if (this.bookingSelectedAppointment && this.bookingSelectedAppointment.id === appointment.id) {
        this.bookingSelectedAppointment = null;
      } else {
        this.bookingSelectedAppointment = appointment;
        this.bookingErrorMessage = '';
      }
    },
    isBookingSlotSelected (appointment) {
      return this.bookingSelectedAppointment && this.bookingSelectedAppointment.id === appointment.id;
    },
    formatBookingTime (datetime) {
      if (!datetime) return '';
      return moment(datetime).format('hh:mm a');
    },
    submitBookConsultation () {
      this.bookingErrorMessage = '';
      if (!this.bookingSelectedAppointment) {
        this.bookingErrorMessage = 'Please select an appointment time.';
        return;
      }
      if (this.bookingTenantId === 'GB' && !this.bookingSelectedAppointment.childAppointmentId) {
        this.bookingErrorMessage = 'The selected Goodbody slot is missing a linked appointment. Please choose another time.';
        return;
      }
      if (this.couponCoversFullFee) {
        this.bookConsultationWithCoupon();
        return;
      }
      this.sendConsultationPaymentLink();
    },
    sendPasswordLink () {
      this.isSendingPasswordLink = true;
      this.$patientService.sendSetPasswordLink(this.patient.id).then(res => {
        const data = (res && res.data) || {};
        this.$awn.success((data.type === 'RESET_PASSWORD' ? 'Password reset link' : 'Set-password link')
          + ' sent to ' + (data.email || this.patient.email));
      }).catch(error => {
        const message = error && error.response && error.response.data && error.response.data.message;
        this.$awn.alert(message || 'Could not send the password link.');
      }).finally(() => {
        this.isSendingPasswordLink = false;
      });
    },
    bookConsultationWithCoupon () {
      this.isBookingConsultation = true;
      const payload = {
        appointmentId: this.bookingSelectedAppointment.id,
        childAppointmentId: this.bookingTenantId === 'GB' ? this.bookingSelectedAppointment.childAppointmentId : null,
        tenantId: this.bookingTenantId,
        mediaType: this.bookingMediaType,
        couponId: this.bookingCouponId ? this.bookingCouponId.trim() : null,
        notes: this.bookingNotes ? this.bookingNotes.trim() : null
      };
      this.$orderService.bookConsultation(this.patient.id, payload)
        .then(result => {
          if (result && result.status === 'success') {
            this.$refs['book-consultation-modal'].hide();
            // The backend emails a set-password link when the patient has no password of their own yet.
            this.$awn.success(this.patient.passwordSetupPending
              ? 'Consultation booked. A set-password email was sent to ' + this.patient.email + '.'
              : 'Consultation booked successfully.');
            const orderItemId = result.data && result.data.orderItemId;
            if (orderItemId) {
              this.$router.push('/order/' + orderItemId);
            } else {
              this.getPatientOrders();
            }
          } else {
            this.bookingErrorMessage = (result && result.message) || 'Failed to book the consultation.';
          }
        })
        .catch(error => {
          const message = error && error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : (error && error.message) || 'Failed to book the consultation.';
          this.bookingErrorMessage = message;
        })
        .finally(() => {
          this.isBookingConsultation = false;
        });
    },
    sendConsultationPaymentLink () {
      const patientUrl = process.env.VUE_APP_PATIENT_URL;
      if (!patientUrl) {
        this.bookingErrorMessage = 'Patient app URL is not configured for this admin environment.';
        return;
      }
      if (!this.patient.email) {
        this.bookingErrorMessage = 'This patient does not have an email address.';
        return;
      }
      this.isBookingConsultation = true;
      const couponId = (this.bookingCouponId || '').trim();
      const patientName = [this.patient.firstName, this.patient.lastName].filter(Boolean).join(' ');
      // Hold the slot first: without it the patient may find it gone and be asked to pick again.
      this.$patientService.holdSlotForPaymentLink(this.patient.id, {
        appointmentId: this.bookingSelectedAppointment.id,
        childAppointmentId: this.bookingTenantId === 'GB' ? (this.bookingSelectedAppointment.childAppointmentId || null) : null,
        tenant: this.bookingTenantId || 'GPS',
        mediaType: this.bookingMediaType || 'VIDEO',
        couponId: couponId || null
      }).then(res => {
        const lead = (res && res.data) || {};
        if (!lead.reference) {
          throw new Error((res && res.message) || 'Could not hold the slot.');
        }
        // The link carries only the booking reference; the patient page loads the rest from the server.
        const link = `${patientUrl}register/admin-booking?ref=${lead.reference}`;
        // Consultation fee from Pricing (GPS) only if the hold didn't return an amount; £39 if Pricing can't be loaded.
        if (Number(lead.amount)) {
          return this.emailConsultationPaymentLink(link, Number(lead.amount), patientName, lead.expiresAt);
        }
        return this.$pricingService.gpsPrice('appointmentPrice', 39.00)
          .then(fee => this.emailConsultationPaymentLink(link, fee, patientName, lead.expiresAt));
      }).catch(error => {
        const message = error && error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : (error && error.message) || 'Could not hold the slot.';
        this.bookingErrorMessage = message;
        this.isBookingConsultation = false;
      });
    },
    emailConsultationPaymentLink (link, amount, patientName, expiresAt) {
      const fee = Number(amount);
      const feeAmount = Number.isFinite(fee) ? Number(fee.toFixed(2)) : 49.00;
      const payload = {
        message: 'Our team has reserved a GP video consultation for you. Please pay within 10 minutes using the link below to confirm your booking. After that the link expires and the slot is released.',
        emailTemplateName: 'prescription-order-summary-payment.html',
        whatsAppTemplate: '',
        subject: 'Complete payment to confirm your GP video consultation',
        isSmsEnable: false,
        isWhatsAppEnable: false,
        isEmailEnable: true,
        type: 'DOCUMENT-PAYMENT-REQUEST',
        heading: 'Complete payment for your GP video consultation',
        url1: link,
        email: [this.patient.email],
        mobile: this.patient.phone ? [this.patient.phone] : [],
        userName: patientName,
        patientNameForInvite: patientName,
        documentType: 'consultation',
        additionalServiceFee: feeAmount,
        prescriptionFee: feeAmount,
        totalCharge: feeAmount,
        notificationCat: 'Consultation payment link'
      };
      return this.$orderService.resendDocumentConsent(payload)
        .then(res => {
          if (res && res.status === 'OK') {
            return this.$patientService.savePaymentLink(this.patient.id, payload.url1)
              .catch(() => null)
              .then(() => {
                this.$refs['book-consultation-modal'].hide();
                this.$awn.success('Payment link sent to ' + this.patient.email);
                this.$refs.paymentLinkModal.show({
                  patientId: this.patient.id,
                  name: patientName,
                  email: this.patient.email,
                  link: payload.url1,
                  expiresAt: expiresAt || null,
                  sent: true,
                  showPatientLink: false
                });
              });
          }
          this.bookingErrorMessage = (res && res.message) || 'Failed to send the payment link.';
        })
        .catch(error => {
          const message = error && error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : (error && error.message) || 'Failed to send the payment link.';
          this.bookingErrorMessage = message;
        })
        .finally(() => {
          this.isBookingConsultation = false;
        });
    },
    updateAdminAlert() {
      const existingAdminAlert = this.patient?.settings?.ADMIN_ALERTS || this.patient?.allAlerts?.admin || this.patient?.adminAlerts || '';
      const newAdminAlert = this.tempAdminAlert?.trim();
      if (existingAdminAlert.length === 0 && newAdminAlert.length === 0) {
        return;
      }
      if (newAdminAlert.length > 250) {
        this.$awn.alert('Message too long. Please shorten to 250 characters.');
        return;
      }
      let _request = this.$patientService.updatePatientSetting;
      if (newAdminAlert.length === 0) {
        _request = this.$patientService.removePatientSetting;
      }
      _request(this.patient.id, 'ADMIN_ALERTS', newAdminAlert)
      .then(response => {
        if (response.success) {
          this.$awn.success('Admin alert updated successfully');
          this.adminAlertEditable = false;
        } else {
          this.$awn.alert(response.message);
        }
      }).catch(error => {
        this.$awn.alert(error.message);
      });
    },
  },
  beforeDestroy () {
    clearTimeout(this.timer);
    OrderEventBus.$off();
  },
  filters: {
    formatDoB: function(date){
      return moment(date).format('DD MMM YYYY');
    }
  }
}
</script>

<style lang="scss" scoped>
  .action-buttons {
    display: flex;
    justify-content: flex-end;
  }
  .margin-1-percent {
    margin-right:1%
  }
  #patientIdDescription small {
    margin-top: -1.8%;
  }
  .btn-group .btn + .btn {
    background-color: #009344 !important;
    border-color: #009344 !important;
  }

  .patientCardCol {
    gap: 16px;
    display: grid;
  }

  .linked-patient-card {
    border: 1px solid #D8D8D8;
    border-radius: 5px;
    padding: 0px 32px;
  }

  .patient-details-wrapper {
    display: flex;
    flex-direction: column;
    gap: 4px;
    border-radius: 5px;
    padding: 12px 0px 16px;
  }

  .freeSub {
    font-weight: 400;
    font-size: 14px;
    line-height: 21px;
    letter-spacing: 0;
    color: #000000;
  }

  .title-wrapper {
    gap: 8px;
    height: 27px;
  }

  .patient-card-heading {
    font-weight: 700;
    font-size: 14px;
    line-height: 21px;
    color: #151B1E;
    margin-bottom: 0px;
  }

  .same-email-tag {
    border-radius: 4px;
    padding: 4px 8px;
    background: #C4E3D6;
    font-weight: 400;
    font-size: 14px;
    line-height: 19px;
    color: #4FAA84;
    margin-bottom: 0px;
  }

  .link-patient-text {
    color: #151B1E;
    margin-bottom: 0px;
  }

  .view-btn {
    color: #ffffff;
    border-radius: 8px;
    padding: 8px 16px;
    margin-top: 12px;
  }

  .mrgx-6 {
    margin-left: -6px !important;
    margin-right: -6px !important;
  }

  .book-consultation-modal {
    padding: 0;

    .book-help-text {
      font-size: 14px;
      line-height: 20px;
      color: #4a4a4a;
      margin-bottom: 16px;
    }

    .input-container {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .input-section {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .model-text {
      font-weight: 400;
      font-size: 16px;
      line-height: 21px;
      color: #000000;
      margin-bottom: 0;
    }

    .custom-select-preview {
      display: flex;
      flex-direction: column;
      padding: 8px 16px 16px;
      gap: 8px;
      border-radius: 8px;
      border: 1px solid #0000001A;
    }

    .clinician-loading {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 0;
      padding: 10px 0;
      color: #646464;
      font-size: 14px;
    }

    .clinician-multiselect {
      font-size: 14px;
    }

    .clinician-multiselect .multiselect__tags {
      min-height: 42px;
      border: 1px solid #d0d7d4;
      border-radius: 8px;
      padding: 8px 40px 0 12px;
    }

    .clinician-multiselect .multiselect__placeholder {
      color: #6b7280;
      margin-bottom: 8px;
      padding-top: 2px;
    }

    .clinician-multiselect .multiselect__single {
      margin-bottom: 8px;
      padding-top: 2px;
      color: #0E2119;
      font-weight: 500;
    }

    .clinician-multiselect .multiselect__input {
      margin-bottom: 8px;
    }

    .clinician-multiselect .multiselect__content-wrapper {
      border: 1px solid #d0d7d4;
      border-radius: 8px;
      margin-top: 4px;
      max-height: 220px;
    }

    .time-slots-container {
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      gap: 8px;
      max-height: 250px;
      overflow-x: hidden;
      overflow-y: auto;
    }

    .no-slots {
      display: flex;
      justify-content: center;
      margin-top: 8px;
      margin-bottom: 0;
      color: #646464;
    }

    .time-slot {
      width: 100%;
      border-radius: 8px;
      padding: 8px 12px;
      font-weight: 400;
      font-size: 14px;
      line-height: 19px;
      color: #2A2A2A;
      background: #F4F4F4;
      text-align: center;
      margin: 0;
      white-space: nowrap;
      cursor: pointer;
      border: 1px solid transparent;
    }

    .time-slot.selected-slot {
      background-color: #dceee6;
      border: 1px solid #4faa84;
    }

    .note-tag {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      border-radius: 8px;
      padding: 8px 12px;
      font-weight: 500;
      font-size: 14px;
      color: #7F1D1D;
      background: #FEF2F2;
      margin: 0;
    }

    .note-tag-success {
      color: #4FAA84;
      background: #DAEDE5;
    }

    .coupon-success {
      font-size: 13px;
      color: #4FAA84;
      margin: 0;
    }

    .saved-card-text {
      font-size: 14px;
      color: #4a4a4a;
      margin: 0;
    }

    .update-button {
      border-radius: 8px;
      padding: 14px 16px;
      background: #48A47E;
      border-color: #48A47E;
      color: #FFFFFF;
      font-weight: 500;
      font-size: 16px;
      line-height: 21px;
      width: 100%;
      margin-top: 8px;
    }
  }
</style>
